import Dexie, { type Table } from "dexie";
import {
  ROLEPLAY_MODULE_ID,
  type Asset,
  type AssetBlob,
  type Character,
  type Chat,
  type GameCard,
  type GeneratedImage,
  type InstalledRqtPack,
  type Lorebook,
  type Message,
  type Persona,
  type Preset,
  type ProviderProfile,
  type Script,
  type Settings,
} from "@roqeet/core";

/**
 * IndexedDB schema (decision #3: Dexie). Index choices are deliberate and back
 * specific decisions:
 *  - messages: compound `[chatId+order]` enables cursor paging without loading
 *    a whole chat (decision #9 / 6s+ memory). `chatId` alone enables counts.
 *  - assets: `hash` is unique -> content-addressed dedup (decision #18).
 *  - assetBlobs: keyed by `hash`, stored separately so metadata queries never
 *    pull blob bytes into memory (decision #18).
 *  - generatedImages: `chatId`, `characterId`, `provider`, `generatedAt` index
 *    the gallery filters (decision #19).
 *
 * Only indexed fields are listed in the schema string; full objects are stored
 * regardless. `sync.deletedAt` tombstones are kept (decision #20) and filtered
 * at query time.
 */
export class RoqeetDB extends Dexie {
  characters!: Table<Character, string>;
  personas!: Table<Persona, string>;
  chats!: Table<Chat, string>;
  lorebooks!: Table<Lorebook, string>;
  presets!: Table<Preset, string>;
  scripts!: Table<Script, string>;
  providerProfiles!: Table<ProviderProfile, string>;
  generatedImages!: Table<GeneratedImage, string>;
  assets!: Table<Asset, string>;
  messages!: Table<Message, string>;
  assetBlobs!: Table<AssetBlob, string>;
  settings!: Table<Settings, string>;
  gameCards!: Table<GameCard, string>;
  installedRqtPacks!: Table<InstalledRqtPack, string>;

  constructor() {
    super("roqeet-rp");

    this.version(1).stores({
      characters: "id, name, *tags, rating, sync.updatedAt",
      personas: "id, name, sync.updatedAt",
      chats: "id, lastMessageAt, pinned, archived, sync.updatedAt",
      lorebooks: "id, name, sync.updatedAt",
      presets: "id, name, sync.updatedAt",
      scripts: "id, name, language, enabled, sync.updatedAt",
      providerProfiles: "id, name, kind, enabled, sync.updatedAt",
      generatedImages:
        "id, chatId, characterId, provider, generatedAt, favorite, sync.updatedAt",
      assets: "id, &hash, mime, refCount, sync.updatedAt",
      // Compound index for paged history (decision #9).
      messages: "id, chatId, [chatId+order], order, sync.updatedAt",
      // Blobs keyed by content hash, separate table (decision #18).
      assetBlobs: "hash",
      settings: "id",
    });

    // v2 (decision #28: generic game session). Additive, zero data loss:
    //  - add `moduleId` index to chats so the module list can query by module,
    //  - backfill existing chats with the roleplay module id (they were all
    //    roleplay sessions before the platform pivot).
    this.version(2)
      .stores({
        chats: "id, moduleId, lastMessageAt, pinned, archived, sync.updatedAt",
      })
      .upgrade(async (tx) => {
        await tx
          .table<Chat, string>("chats")
          .toCollection()
          .modify((chat) => {
            if (!chat.moduleId) chat.moduleId = ROLEPLAY_MODULE_ID;
          });
      });

    // v3 (decision #29: data-type game cards). Purely additive — a new table
    // for user-authored game definitions. No backfill needed (existing installs
    // simply start with zero cards). Cards are compiled to GameModules at boot.
    this.version(3).stores({
      gameCards: "id, enabled, sync.updatedAt",
    });

    // v4 (Roqeet 0.2): local `.rqp` install registry. Purely additive;
    // existing 0.1 data remains untouched and the registry starts empty.
    this.version(4).stores({
      installedRqtPacks: "id, packageId, name, type, enabled, installedAt, sync.updatedAt",
    });
  }
}

let _db: RoqeetDB | null = null;

/** Lazily-created singleton DB handle. */
export function getDB(): RoqeetDB {
  if (!_db) _db = new RoqeetDB();
  return _db;
}
